import React, { useEffect, useMemo, useState } from "react";
import ReportTablePage from "./ReportTablePage";
import {
  getMemberRegisterReport,
  getReportLookups,
} from "../../services/api";

export default function MemberRegisterReportPage() {
  const [rows, setRows] = useState([]);
  const [lookups, setLookups] = useState({
    kanda: [],
    jumuia: [],
  });
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    gender: "",
    maritalStatus: "",
    ageGroup: "",
    status: "",
    kandaId: "",
    jumuiaId: "",
    dateFrom: "",
    dateTo: "",
  });

  useEffect(() => {
    loadLookups();
    loadReport();
  }, []);

  const loadLookups = async () => {
    try {
      const res = await getReportLookups();
      setLookups(res.data || {});
    } catch (err) {
      console.error(err);
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      const res = await getMemberRegisterReport(filters);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      gender: "",
      maritalStatus: "",
      ageGroup: "",
      status: "",
      kandaId: "",
      jumuiaId: "",
      dateFrom: "",
      dateTo: "",
    });
  };

  const filteredJumuia = useMemo(() => {
    if (!filters.kandaId) return lookups.jumuia || [];
    return (lookups.jumuia || []).filter(
      (j) => String(j.kanda_id) === String(filters.kandaId)
    );
  }, [lookups.jumuia, filters.kandaId]);

  // Columns definition: each field has its own column for clean export
  const columns = [
    { label: "Full Name", key: "full_name", render: (r) => r.full_name || "-", exportValue: (r) => r.full_name || "" },
    { label: "Member Code", key: "member_code", render: (r) => r.member_code || "-", exportValue: (r) => r.member_code || "" },
    { label: "Phone", key: "phone", render: (r) => r.phone || "-", exportValue: (r) => r.phone || "" },
    { label: "Gender", key: "gender", render: (r) => r.gender || "-", exportValue: (r) => r.gender || "" },
    { label: "Marital Status", key: "marital_status", render: (r) => r.marital_status || "-", exportValue: (r) => r.marital_status || "" },
    { label: "Age Group", key: "age_group", render: (r) => r.age_group || "-", exportValue: (r) => r.age_group || "" },
    { label: "Student", key: "is_student", render: (r) => (r.is_student ? "Yes" : "No"), exportValue: (r) => (r.is_student ? "Yes" : "No") },
    { label: "Jimbo", key: "jimbo_name", render: (r) => r.jimbo_name || "-", exportValue: (r) => r.jimbo_name || "" },
    { label: "Parokia", key: "parokia_name", render: (r) => r.parokia_name || "-", exportValue: (r) => r.parokia_name || "" },
    { label: "Kanda", key: "kanda_name", render: (r) => r.kanda_name || "-", exportValue: (r) => r.kanda_name || "" },
    { label: "Jumuiya", key: "jumuia_name", render: (r) => r.jumuia_name || "-", exportValue: (r) => r.jumuia_name || "" },
    { label: "Region", key: "region_name", render: (r) => r.region_name || "-", exportValue: (r) => r.region_name || "" },
    { label: "District", key: "district_name", render: (r) => r.district_name || "-", exportValue: (r) => r.district_name || "" },
    { label: "Residence", key: "residence_area", render: (r) => r.residence_area || "-", exportValue: (r) => r.residence_area || "" },
    { label: "Status", key: "status", render: (r) => r.status || "-", exportValue: (r) => r.status || "" },
  ];

  // Filters UI
  const filterNodes = (
    <>
      <div>
        <label className="field-label">Date From</label>
        <input type="date" className="input" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
      </div>
      <div>
        <label className="field-label">Date To</label>
        <input type="date" className="input" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
      </div>
      <div>
        <label className="field-label">Search</label>
        <input className="input" placeholder="Name, code, phone..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
      </div>
      <div>
        <label className="field-label">Gender</label>
        <select className="input" value={filters.gender} onChange={(e) => setFilters({ ...filters, gender: e.target.value })}>
          <option value="">All</option>
          <option value="MALE">MALE</option>
          <option value="FEMALE">FEMALE</option>
        </select>
      </div>
      <div>
        <label className="field-label">Marital Status</label>
        <select className="input" value={filters.maritalStatus} onChange={(e) => setFilters({ ...filters, maritalStatus: e.target.value })}>
          <option value="">All</option>
          <option value="SINGLE">SINGLE</option>
          <option value="MARRIED">MARRIED</option>
          <option value="DIVORCED">DIVORCED</option>
          <option value="WIDOWED">WIDOWED</option>
        </select>
      </div>
      <div>
        <label className="field-label">Age Group</label>
        <select className="input" value={filters.ageGroup} onChange={(e) => setFilters({ ...filters, ageGroup: e.target.value })}>
          <option value="">All</option>
          <option value="CHILD">CHILD</option>
          <option value="YOUTH">YOUTH</option>
          <option value="ADULT">ADULT</option>
          <option value="ELDER">ELDER</option>
        </select>
      </div>
      <div>
        <label className="field-label">Status</label>
        <select className="input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All</option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="INACTIVE">INACTIVE</option>
        </select>
      </div>
      <div>
        <label className="field-label">Kanda</label>
        <select className="input" value={filters.kandaId} onChange={(e) => setFilters({ ...filters, kandaId: e.target.value, jumuiaId: "" })}>
          <option value="">All</option>
          {(lookups.kanda || []).map((k) => <option key={k.kanda_id} value={k.kanda_id}>{k.kanda_name}</option>)}
        </select>
      </div>
      <div>
        <label className="field-label">Jumuiya</label>
        <select className="input" value={filters.jumuiaId} onChange={(e) => setFilters({ ...filters, jumuiaId: e.target.value })}>
          <option value="">All</option>
          {filteredJumuia.map((j) => <option key={j.jumuia_id} value={j.jumuia_id}>{j.jumuia_name}</option>)}
        </select>
      </div>
    </>
  );

  // Updated rows render with nowrap
  const renderRows = (
    <tbody>
      {rows.map((row, idx) => (
        <tr key={idx}>
          {columns.map((col) => (
            <td
              key={col.key}
              style={{ whiteSpace: "nowrap", padding: "8px", border: "1px solid #ccc" }}
            >
              {col.render ? col.render(row) : row[col.key]}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  return (
    <ReportTablePage
      title="Member Register Report"
      filters={filterNodes}
      rows={rows}
      columns={columns}
      loading={loading}
      onSearch={loadReport}
      onClear={clearFilters}
      exportFileName="member_register_report.csv"
      tableBody={renderRows} // pass the updated tbody
    />
  );
}